import React, { useEffect, useMemo, useRef, useState } from 'react';
import confetti from 'canvas-confetti';
import {
  Trophy,
  Coins,
  TrendingUp,
  TrendingDown,
  Flame,
  Target,
  Home,
  RotateCcw,
  Download,
  FileSpreadsheet,
  FileText,
  Loader2,
} from 'lucide-react';
import type { PodiumEntry, GameReport, TeamStanding } from '../../shared/protocol';
import { downloadFile, reportFileName, reportToCsv, reportToHtml } from '../lib/report';
import { BRAND, GRADIENT, TEAM_COLORS, type TeamId } from '../brand';
import { Avatar } from './Avatar';
import { playFanfareSound, playRevealSting } from '../lib/sound';

interface Props {
  podium: PodiumEntry[];
  /** Partida por equipos: el podio se muestra por equipo, no por jugador. */
  teams?: TeamStanding[];
  prizePool: number;
  myId: string;
  /** Informe pregunta por pregunta; completo si eres el anfitrión. */
  report: GameReport | null;
  isHost: boolean;
  onHome: () => void;
  onPlayAgain?: () => void;
}

/** Alturas relativas del pedestal: oro, plata, bronce. */
const STEP_HEIGHT = [150, 110, 82];
const MEDAL_TONE = [
  'from-ember-400 to-ember-600',
  'from-ink-300 to-ink-500',
  'from-ember-700 to-ink-600',
];

/**
 * Revelación por etapas: menciones (4.º y 5.º), luego 3.º, 2.º y por último 1.º.
 * Cada paso indica cuánto se espera ANTES de mostrarlo; la pausa antes del
 * primer lugar es la más larga, para el suspense.
 */
type RevealStep = 'mentions' | 3 | 2 | 1 | 'rest';
const STEP_DELAY: Record<RevealStep, number> = {
  mentions: 900,
  3: 2000,
  2: 2200,
  1: 2800,
  rest: 1600,
};
const STEP_CAPTION: Partial<Record<RevealStep, string>> = {
  mentions: 'Antes del podio, las menciones honoríficas…',
  3: 'En tercer lugar…',
  2: 'En segundo lugar…',
  1: 'Y el primer lugar es…',
};

function revealSteps(count: number): RevealStep[] {
  const steps: RevealStep[] = [];
  if (count > 3) steps.push('mentions');
  if (count >= 3) steps.push(3);
  if (count >= 2) steps.push(2);
  if (count >= 1) steps.push(1);
  steps.push('rest');
  return steps;
}

/** Duración total de la revelación con el máximo de pasos (para no taparla con modales). */
export const PODIUM_REVEAL_MS = Object.values(STEP_DELAY).reduce((a, b) => a + b, 0);

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export const Podium: React.FC<Props> = ({
  podium,
  teams,
  prizePool,
  myId,
  report,
  isHost,
  onHome,
  onPlayAgain,
}) => {
  const top3 = podium.slice(0, 3);
  const mentions = podium.slice(3, 5);
  const rest = podium.slice(5);
  const me = podium.find((p) => p.id === myId);
  const byTeams = !!teams && teams.length > 0;
  // Por equipos, el puesto es el de mi equipo.
  const myPlacement = byTeams
    ? teams.findIndex((t) => t.team === (me?.team ?? '')) + 1
    : podium.findIndex((p) => p.id === myId) + 1;

  const total = byTeams ? teams.length : podium.length;
  const steps = useMemo(() => revealSteps(total), [total]);
  // Nadie se salta la revelación: ni con un botón ni con "reducir movimiento"
  // (en ese caso solo se quitan las animaciones, no las pausas).
  const [revealed, setRevealed] = useState(0);
  const done = revealed >= steps.length;
  const shownSteps = new Set(steps.slice(0, revealed));
  const isShown = (place: number) =>
    place <= 3 ? shownSteps.has(place as RevealStep) : shownSteps.has('mentions');
  const caption = done ? null : STEP_CAPTION[steps[revealed]];

  // Avanza un paso cada vez, con la pausa propia del siguiente.
  useEffect(() => {
    if (done) return;
    const t = setTimeout(() => setRevealed((n) => n + 1), STEP_DELAY[steps[revealed]]);
    return () => clearTimeout(t);
  }, [revealed, done, steps]);

  // Cada puesto entra con un golpe y el podio completo, con fanfarria.
  useEffect(() => {
    if (revealed === 0) return;
    if (done) playFanfareSound();
    else playRevealSting();
  }, [revealed, done]);

  // Confeti justo cuando se revela MI puesto. Quien no subió al podio no
  // necesita una celebración en la cara.
  const fired = useRef(false);
  const myPlaceShown = myPlacement >= 1 && myPlacement <= 3 && isShown(myPlacement);
  useEffect(() => {
    if (!myPlaceShown || fired.current) return;
    fired.current = true;
    if (prefersReducedMotion()) return;

    const colors = [GRADIENT.from, GRADIENT.via, GRADIENT.to, '#E1467C'];
    confetti({ particleCount: 90, spread: 75, origin: { y: 0.6 }, colors });
    const t = setTimeout(
      () => confetti({ particleCount: 60, spread: 100, origin: { y: 0.55 }, colors }),
      350,
    );
    return () => clearTimeout(t);
  }, [myPlaceShown]);

  // Orden visual del pedestal: 2.º, 1.º, 3.º.
  const arranged = [top3[1], top3[0], top3[2]].filter(Boolean);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-10">
      <div className="text-center space-y-2">
        <Trophy className="w-12 h-12 mx-auto text-ember-400" />
        <h1 className="text-4xl font-display font-black text-brand-gradient">Resultados</h1>
        {prizePool > 0 && (
          <p className="text-ink-400">
            Bote repartido:{' '}
            <span className="text-ember-300 font-semibold">
              {prizePool.toLocaleString('es')} {BRAND.currencyName}
            </span>
          </p>
        )}
        {/* Reserva su alto para que nada salte al terminar la revelación. */}
        <div className="h-8 flex items-center justify-center">
          {caption && (
            <p key={caption} className="text-lg font-semibold text-ink-200 animate-rise">
              {caption}
            </p>
          )}
        </div>
      </div>

      {byTeams && <TeamPodium teams={teams} myTeam={me?.team} isShown={isShown} />}

      {/* Pedestal */}
      {!byTeams && (
      <div className="flex items-end justify-center gap-3 md:gap-6">
        {arranged.map((entry) => {
          const place = top3.indexOf(entry) + 1;
          const shown = isShown(place);
          return (
            <div
              key={entry.id}
              className="flex flex-col items-center gap-3 animate-rise"
              style={{ animationDelay: `${(4 - place) * 120}ms` }}
            >
              {shown ? (
                <div className={`animate-pop ${place === 1 ? 'podium-winner' : ''}`}>
                  <Avatar config={entry.avatar as never} size={place === 1 ? 76 : 58} />
                </div>
              ) : (
                <MysterySlot size={place === 1 ? 76 : 58} />
              )}
              <div className="text-center w-[7rem] md:w-[10rem] min-h-[3.25rem]">
                {shown ? (
                  <div className="animate-rise">
                    <p
                      className={`font-semibold text-ink-50 truncate ${
                        place === 1 ? 'text-lg' : ''
                      }`}
                    >
                      {entry.displayName}
                    </p>
                    <p className="font-mono text-ink-300 tabular-nums">
                      {entry.score.toLocaleString('es')}
                    </p>
                    {entry.dahusAwarded > 0 && (
                      <span className="chip mt-1.5 border-ember-500 bg-ember-500/15 text-ember-300">
                        <Coins className="w-3 h-3" />+{entry.dahusAwarded}
                      </span>
                    )}
                  </div>
                ) : (
                  <p className="font-mono text-ink-600">• • •</p>
                )}
              </div>
              <div
                className={`w-20 md:w-28 rounded-t-2xl bg-gradient-to-b ${MEDAL_TONE[place - 1]} flex items-start justify-center pt-3 transition-[filter] duration-500 ${
                  shown ? '' : 'saturate-0 brightness-75'
                }`}
                style={{ height: STEP_HEIGHT[place - 1] }}
              >
                <span className="text-3xl font-display font-black text-on-accent">{place}</span>
              </div>
            </div>
          );
        })}
      </div>
      )}

      {/* Menciones honoríficas: 4.º y 5.º, discretas frente al podio. */}
      {!byTeams && mentions.length > 0 && (
        <HonorableMentions
          shown={isShown(4)}
          items={mentions.map((p, i) => ({
            key: p.id,
            place: i + 4,
            label: p.displayName,
            score: p.score,
            avatar: p.avatar,
            mine: p.id === myId,
          }))}
        />
      )}

      {done && (
      <div className="space-y-10 animate-rise">
      {/* Tu resultado */}
      {me && (
        <div className="surface-raised p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-lg">Tu partida</h2>
            <span className="chip border-ink-600 bg-ink-800 text-ink-300">
              {byTeams
                ? `Tu equipo: puesto ${myPlacement} de ${teams.length}`
                : `Puesto ${myPlacement} de ${podium.length}`}
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Puntos" value={me.score.toLocaleString('es')} />
            <Stat
              label="Aciertos"
              value={`${Math.round(me.accuracy * 100)}%`}
              icon={<Target className="w-4 h-4" />}
            />
            <Stat
              label="Mejor racha"
              value={String(me.bestStreak)}
              icon={<Flame className="w-4 h-4" />}
            />
            <Stat
              label="D-Rating"
              value={String(me.newRating)}
              trend={me.ratingChange}
            />
          </div>

          {me.dahusAwarded > 0 && (
            <div className="flex items-center gap-3 p-4 rounded-xl bg-ember-500/12 border border-ember-600">
              <Coins className="w-6 h-6 text-ember-400 shrink-0" />
              <p className="text-ink-100">
                Ganaste{' '}
                <span className="text-ember-300 font-bold">
                  {me.dahusAwarded.toLocaleString('es')} {BRAND.currencyName}
                </span>{' '}
                del bote.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Resto de la tabla */}
      {!byTeams && rest.length > 0 && (
        <div className="surface p-4">
          <h3 className="text-sm uppercase tracking-wider text-ink-400 mb-3">Resto de la sala</h3>
          <ol className="space-y-1.5">
            {rest.map((p, i) => (
              <li
                key={p.id}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg ${
                  p.id === myId ? 'bg-sky-500/15 border border-sky-700' : 'bg-ink-850'
                }`}
              >
                <span className="w-6 text-center text-ink-500 font-bold">{i + 6}</span>
                <Avatar config={p.avatar} size={32} />
                <span className="flex-1 min-w-0 truncate text-ink-100">{p.displayName}</span>
                {p.ratingChange !== 0 && (
                  <span
                    className={`text-xs font-mono ${
                      p.ratingChange > 0 ? 'text-success-400' : 'text-danger-400'
                    }`}
                  >
                    {p.ratingChange > 0 ? '+' : ''}
                    {p.ratingChange}
                  </span>
                )}
                <span className="font-mono tabular-nums text-ink-300">
                  {p.score.toLocaleString('es')}
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {(isHost || me) && <ReportDownload report={report} isHost={isHost} />}

      <div className="flex justify-center gap-3">
        <button
          onClick={onHome}
          className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold bg-ink-800 hover:bg-ink-700 border border-ink-600 text-ink-100 transition-colors"
        >
          <Home className="w-4 h-4" />
          Inicio
        </button>
        {onPlayAgain && (
          <button
            onClick={onPlayAgain}
            className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold bg-ember-500 hover:bg-ember-400 text-on-accent transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Otra partida
          </button>
        )}
      </div>
      </div>
      )}
    </div>
  );
};

/** Hueco del podio aún sin revelar. */
const MysterySlot: React.FC<{ size: number }> = ({ size }) => (
  <div
    className="rounded-full border-2 border-dashed border-ink-600 bg-ink-850 flex items-center justify-center animate-pulse"
    style={{ width: size, height: size }}
  >
    <span className="font-display font-black text-ink-500 text-2xl">?</span>
  </div>
);

interface MentionItem {
  key: string;
  place: number;
  label: React.ReactNode;
  score: number;
  avatar?: PodiumEntry['avatar'];
  mine: boolean;
}

/** 4.º y 5.º puesto: aparecen antes del podio, pero en pequeño y sin brillo. */
const HonorableMentions: React.FC<{ shown: boolean; items: MentionItem[] }> = ({
  shown,
  items,
}) => (
  // Ocupa su sitio desde el principio para que el podio no se mueva al aparecer.
  <div className={`max-w-xl mx-auto ${shown ? 'animate-rise' : 'invisible'}`}>
    <p className="text-center text-xs uppercase tracking-wider text-ink-500 mb-2">
      Menciones honoríficas
    </p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {items.map((m) => (
        <div
          key={m.key}
          className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border ${
            m.mine ? 'bg-sky-500/15 border-sky-700' : 'bg-ink-850 border-ink-700'
          }`}
        >
          <span className="text-sm text-ink-500 font-bold w-7">{m.place}.º</span>
          {m.avatar !== undefined && <Avatar config={m.avatar} size={28} />}
          <span className="flex-1 min-w-0 truncate text-sm text-ink-200">{m.label}</span>
          <span className="font-mono text-sm tabular-nums text-ink-400">
            {m.score.toLocaleString('es')}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const teamLabel = (t: TeamStanding) =>
  TEAM_COLORS[t.team as TeamId]?.label ?? (t.team || 'Sin equipo');

const TeamPodium: React.FC<{
  teams: TeamStanding[];
  myTeam?: string;
  isShown: (place: number) => boolean;
}> = ({ teams, myTeam, isShown }) => (
  <div className="space-y-6">
    <div className="flex items-end justify-center gap-3 md:gap-6">
      {[teams[1], teams[0], teams[2]].filter(Boolean).map((t) => {
        const place = teams.indexOf(t) + 1;
        const style = TEAM_COLORS[t.team as TeamId];
        const shown = isShown(place);
        return (
          <div
            key={t.team}
            className="flex flex-col items-center gap-3 animate-rise"
            style={{ animationDelay: `${(4 - place) * 120}ms` }}
          >
            {shown ? (
              <div className="text-center animate-pop">
                <span
                  className={`chip text-sm ${style?.chip ?? 'border-ink-600 bg-ink-800 text-ink-300'} ${
                    place === 1 ? 'podium-winner' : ''
                  }`}
                >
                  {teamLabel(t)}
                </span>
                <p className="font-mono text-ink-300 tabular-nums mt-3">
                  {t.score.toLocaleString('es')}
                </p>
                <p className="text-xs text-ink-500">
                  {t.members} {t.members === 1 ? 'jugador' : 'jugadores'}
                </p>
              </div>
            ) : (
              <MysterySlot size={58} />
            )}
            <div
              className={`w-24 md:w-32 rounded-t-2xl bg-gradient-to-b ${MEDAL_TONE[place - 1]} flex items-start justify-center pt-3 transition-[filter] duration-500 ${
                shown ? '' : 'saturate-0 brightness-75'
              }`}
              style={{ height: STEP_HEIGHT[place - 1] }}
            >
              <span className="text-3xl font-display font-black text-on-accent">{place}</span>
            </div>
          </div>
        );
      })}
    </div>

    {teams.length > 3 && (
      <HonorableMentions
        shown={isShown(4)}
        items={teams.slice(3, 5).map((t, i) => ({
          key: t.team,
          place: i + 4,
          label: teamLabel(t),
          score: t.score,
          mine: t.team === (myTeam ?? ''),
        }))}
      />
    )}

    {teams.length > 5 && isShown(1) && (
      <div className="surface p-4 animate-rise">
        <ol className="space-y-1.5">
          {teams.slice(5).map((t, i) => (
            <li
              key={t.team}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg ${
                t.team === (myTeam ?? '') ? 'bg-sky-500/15 border border-sky-700' : 'bg-ink-850'
              }`}
            >
              <span className="w-6 text-center text-ink-500 font-bold">{i + 6}</span>
              <span className="flex-1 text-ink-100">
                {TEAM_COLORS[t.team as TeamId]?.label ?? (t.team || 'Sin equipo')}
              </span>
              <span className="font-mono tabular-nums text-ink-300">
                {t.score.toLocaleString('es')}
              </span>
            </li>
          ))}
        </ol>
      </div>
    )}
  </div>
);

const Stat: React.FC<{
  label: string;
  value: string;
  icon?: React.ReactNode;
  trend?: number;
}> = ({ label, value, icon, trend }) => (
  <div className="bg-ink-850 border border-ink-700 rounded-xl p-3">
    <p className="text-ink-500 text-xs flex items-center gap-1.5 mb-1">
      {icon}
      {label}
    </p>
    <p className="text-xl font-bold text-ink-50 font-mono tabular-nums flex items-center gap-1.5">
      {value}
      {trend !== undefined && trend !== 0 && (
        <span
          className={`text-xs flex items-center ${
            trend > 0 ? 'text-success-400' : 'text-danger-400'
          }`}
        >
          {trend > 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
          {trend > 0 ? '+' : ''}
          {trend}
        </span>
      )}
    </p>
  </div>
);

const ReportDownload: React.FC<{ report: GameReport | null; isHost: boolean }> = ({
  report,
  isHost,
}) => {
  const full = report?.scope === 'full';
  const btn =
    'flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold bg-ink-800 hover:bg-ink-700 border border-ink-600 text-ink-100 transition-colors disabled:opacity-50';

  return (
    <div className="surface p-5 space-y-3">
      <div className="flex items-start gap-3">
        <Download className="w-5 h-5 text-ember-400 shrink-0 mt-0.5" />
        <div>
          <h3 className="font-bold">
            {isHost ? 'Descargar resultados de la partida' : 'Descargar mi resultado'}
          </h3>
          <p className="text-sm text-ink-400">
            {isHost
              ? 'Pregunta por pregunta de cada jugador: qué respondió, dónde falló, puntos, tiempos y estadísticas de la sala.'
              : 'Tus respuestas pregunta por pregunta, cuáles fallaste, puntos y tiempos.'}
          </p>
        </div>
      </div>

      {!report ? (
        <p className="text-sm text-ink-400 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Preparando el informe…
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          <button
            className={btn}
            onClick={() =>
              downloadFile(reportFileName(report, 'html'), reportToHtml(report), 'text/html')
            }
          >
            <FileText className="w-4 h-4" />
            {full ? 'Informe completo (HTML)' : 'Informe (HTML)'}
          </button>
          <button
            className={btn}
            onClick={() =>
              downloadFile(reportFileName(report, 'csv'), reportToCsv(report), 'text/csv')
            }
          >
            <FileSpreadsheet className="w-4 h-4" />
            Hoja de cálculo (CSV)
          </button>
        </div>
      )}
    </div>
  );
};
